import { formatDate } from "@/lib/utils";
import type { Order } from '@/shared/schemas';
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

import { getCompanyConfig } from "@/config/company";

interface CompanyConfig {
    companyName?: string;
    companyNameEn?: string;
    logoUrl?: string;
}

interface OrderProductionPrintTemplateProps {
    order: Order;
    config?: CompanyConfig;
    showImages?: boolean;
}

import { formatProductSpecs } from "@/lib/format-specs";

import { toVnDateString } from "@apv-erp/status";
/**
 * Production Request Template (Phiếu Yêu Cầu Sản Xuất)
 * Used by Factory/Production to prepare materials and plan production based on Sales Order.
 */
const parseSpecs = formatProductSpecs;

export default function OrderProductionPrintTemplate({ order, config, showImages = false }: OrderProductionPrintTemplateProps) {
    const c = getCompanyConfig(config);
    const details = (order.orderDetails || []) as any[];

    // Parse product specs JSON to extract printSize, purchaseSize, outs, etc.
    const parsedDetails = details.map((d: any) => {
        let parsedSpecs: any = {};
        if (d.product?.specs) {
            try {
                parsedSpecs = typeof d.product.specs === 'string' ? JSON.parse(d.product.specs) : d.product.specs;
            } catch { /* ignore */ }
        }
        return {
            ...d,
            parsedSpecs
        };
    });

    return (
        <div className="kh-print-doc p-[10mm] text-black leading-relaxed text-[11px] bg-white w-full h-full mx-auto min-h-[297mm]" style={{ fontFamily: "'Times New Roman', Times, serif" }} id="print-area">
            <style>{`
                /* No inline media print styles needed, managed by PrintDialog */
                .prod-table td, .prod-table th { border: 1px solid #000; }
                .bg-gray-header { background-color: #f1f5f9; !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            `}</style>

            {/* ========== COMPACT HEADER ========== */}
            <table className="w-full border-collapse mb-3" style={{ border: "2px solid #000" }}>
                <tbody>
                    <tr>
                        <td className="w-[20%] text-center align-middle border border-black p-2">
                            {c.logoUrl ? (
                                <img src={c.logoUrl} alt="Logo" className="max-h-[45px] mx-auto object-contain" />
                            ) : (
                                <span className="font-bold text-[14px] uppercase">{c.companyName}</span>
                            )}
                        </td>
                        <td className="w-[80%] border border-black p-2 text-center align-middle">
                            <div className="font-bold text-[13px] uppercase">{c.companyName}</div>
                            <div className="text-[10px] mt-0.5">{c.address1 || c.address2}</div>
                            <div className="text-[10px] mt-0.5">ĐT: {c.phone} | Email: {c.email}</div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* DOCUMENT TITLE AND INFO BLOCK */}
            <PrintDocumentTitle
                title="PHIẾU YÊU CẦU SẢN XUẤT"
                subtitle="Production Requisition Form"
            />

            <PrintInfoGrid
                leftColumn={[
                    {
                        label: "Khách hàng",
                        labelEn: "Customer",
                        value: order.customer?.name || order.customer?.shortName || '—',
                        valueClassName: "text-blue-900 font-bold",
                    },
                    {
                        label: "Lệnh SX",
                        labelEn: "Order No.",
                        value: order.orderId,
                        valueClassName: "text-[13px] text-red-700 font-mono font-bold",
                    },
                    {
                        label: "Mã PO khách",
                        labelEn: "PO Ref",
                        value: order.orderDetails?.[0]?.poReference || '—',
                    },
                ]}
                rightColumn={[
                    {
                        label: "Ngày lập",
                        labelEn: "Date",
                        value: formatDate(new Date()),
                    },
                    {
                        label: "Hạn giao",
                        labelEn: "Deadline",
                        value: order.deliveryDeadline ? formatDate(order.deliveryDeadline) : 'Thỏa thuận',
                        valueClassName: "text-red-700 font-bold",
                    },
                    {
                        label: "Ghi chú",
                        labelEn: "Notes",
                        value: order.notes || '—',
                    },
                ]}
            />


            {/* ========== PRODUCTION TABLE ========== */}
            {(() => {
                const showImagesCol = !!showImages;
                const remaining = 100 - 3 - 14 - (showImagesCol ? 10 : 0) - 7 - 7 - 5 - 8 - 11;
                const productNameWidth = `${Math.floor(remaining * 0.45)}%`;
                const detailsWidth = `${remaining - Math.floor(remaining * 0.45)}%`;

                return (
                    <table className="prod-table w-full border-collapse text-[11px] text-center mb-6 table-fixed">
                        <thead>
                            <tr className="bg-gray-header font-bold text-[11px]">
                                <th style={{ width: '3%' }} className="px-1 py-2 text-center">STT</th>
                                <th style={{ width: '14%' }} className="px-1 py-2 text-left">Mã SP</th>
                                <th style={{ width: productNameWidth }} className="px-2 py-2 text-left">Tên sản phẩm<br/><span className="font-normal italic">(Quy cách)</span></th>
                                <th style={{ width: detailsWidth }} className="px-2 py-2 text-left">Chi tiết<br/><span className="font-normal italic">(Mô tả chi tiết, Khổ in/mua, Số bát)</span></th>
                                {showImagesCol && <th style={{ width: '10%' }} className="px-1 py-2 text-center">Hình</th>}
                                <th style={{ width: '7%' }} className="px-1 py-2 text-center">SL đặt</th>
                                <th style={{ width: '7%' }} className="px-1 py-2 text-center">Lưu kho</th>
                                <th style={{ width: '5%' }} className="px-1 py-2 text-center">% SL</th>
                                <th style={{ width: '8%' }} className="px-1 py-2 text-center">SL kế hoạch</th>
                                <th style={{ width: '11%' }} className="px-1 py-2 text-left">Ghi chú SX</th>
                            </tr>
                        </thead>
                <tbody>
                    {parsedDetails.length === 0 ? (
                        <tr>
                            <td colSpan={showImages ? 10 : 9} className="border border-black p-4 italic text-slate-500">
                                Không có sản phẩm
                            </td>
                        </tr>
                    ) : (() => {
                        const getMaterial = (item: any) => {
                            let materialStr = '';
                            if (item.product?.productMaterials && item.product.productMaterials.length > 0) {
                                materialStr = item.product.productMaterials.map((m: any) => {
                                    const code = m.material?.materialId;
                                    const name = m.material?.name || m.materialId;
                                    return code ? `${code} - ${name}` : name;
                                }).join(', ');
                            }
                            if (materialStr) return materialStr;

                            const tagsArray = (item.tags || item.product?.tags || '').split(',').map((s: string) => s.trim()).filter(Boolean);
                            const fallbackMaterial = tagsArray.length > 0 ? tagsArray[0] : '—';
                            return item.parsedSpecs?.material || item.materialName || item.product?.material?.name || fallbackMaterial;
                        };

                        const normalized = parsedDetails.map((d: any) => ({
                            ...d,
                            _sortMat: getMaterial(d).toLowerCase().trim(),
                            _sortProc: (d.product?.productProcesses?.map((p: any) => p.process?.name).filter(Boolean).join(', ') || d.process || d.product?.process || '—').toLowerCase().trim(),
                            // Sản phẩm khác mã nhưng trùng tên không được gộp chung ô (FB-9BRTHU)
                            _sortProduct: d.productId || d.product?.id || d.productCode || d.product?.productCode || '',
                            _sortName: (d.productName || d.product?.name || '').toLowerCase().trim(),
                            _sortSpec: parseSpecs(d.specs),
                        }));

                        const sorted = normalized.sort((a: any, b: any) => {
                            if (a._sortMat !== b._sortMat) return a._sortMat.localeCompare(b._sortMat);
                            if (a._sortProc !== b._sortProc) return a._sortProc.localeCompare(b._sortProc);
                            if (a._sortName !== b._sortName) return a._sortName.localeCompare(b._sortName);
                            return a._sortSpec.localeCompare(b._sortSpec);
                        });

                        const spanMatProc: number[] = new Array(sorted.length).fill(0);
                        let gi = 0;
                        while (gi < sorted.length) {
                            let gj = gi + 1;
                            while (gj < sorted.length && sorted[gj]._sortMat === sorted[gi]._sortMat && sorted[gj]._sortProc === sorted[gi]._sortProc) gj++;
                            spanMatProc[gi] = gj - gi;
                            gi = gj;
                        }

                        const spanProduct: number[] = new Array(sorted.length).fill(0);
                        gi = 0;
                        while (gi < sorted.length) {
                            let gj = gi + 1;
                            while (
                                gj < sorted.length &&
                                sorted[gj]._sortMat === sorted[gi]._sortMat &&
                                sorted[gj]._sortProc === sorted[gi]._sortProc &&
                                sorted[gj]._sortName === sorted[gi]._sortName &&
                                sorted[gj]._sortProduct === sorted[gi]._sortProduct &&
                                sorted[gj]._sortSpec === sorted[gi]._sortSpec
                            ) gj++;
                            spanProduct[gi] = gj - gi;
                            gi = gj;
                        }

                        return sorted.map((item: any, index: number) => {
                            const productName = item.productName || item.product?.name || '';
                            const qty = Number(item.quantity) || 0;
                            const rawProcess = item.product?.productProcesses?.map((p: any) => p.process?.name).filter(Boolean).join(', ') || item.process || item.product?.process || '—';
                            const process = String(rawProcess).replace(/\+/g, ', ');
                            
                            const material = getMaterial(item);
                            
                            // Fields extracted from parsed specs mapping
                            const printSize = item.parsedSpecs?.printSize || '—';
                            const purchaseSize = item.parsedSpecs?.purchaseSize || '—';
                            const outs = item.parsedSpecs?.outs || '—';
                            // Ghi chú SX: ghi chú dòng đơn hàng (sale nhập trên form) + ghi chú quy cách sản phẩm
                            const prodNotes = [item.notes, item.parsedSpecs?.notes].filter((n: any) => n && String(n).trim()).join(' · ') || '—';

                            const showMergeProduct = spanProduct[index] > 0;
                            const showMergeMatProc = spanMatProc[index] > 0;

                            const itemDeadline = item.deliveryDeadline ? toVnDateString(item.deliveryDeadline) : null;
                            const orderDeadline = order.deliveryDeadline ? toVnDateString(order.deliveryDeadline) : null;
                            const isDiffDeadline = itemDeadline && itemDeadline !== orderDeadline;
                            const fmtDMY = (ds: string) => { const [y, m, d] = ds.split('-'); return `${d}/${m}/${y}`; };

                            const specsText = parseSpecs(item.specs || item.product?.specs);
                            let processText = process;
                            if (specsText && processText !== '—') {
                                const escapedSpecs = specsText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const specRegex = new RegExp(`(?:,\\s*)?${escapedSpecs}(?:\\s*,)?`, 'gi');
                                processText = processText.replace(specRegex, ', ').replace(/,\s*$/, '').replace(/^,\s*/, '').trim();
                            }

                            return (
                                <tr key={item.id || index} className="break-inside-avoid">
                                    <td className="px-1 py-2">{index + 1}</td>
                                    {showMergeProduct && (
                                        <td className="px-2 py-2 text-left align-top font-mono text-slate-700 break-words" rowSpan={spanProduct[index]}>
                                            {item.productCode || item.product?.productCode || '—'}
                                        </td>
                                    )}
                                    {showMergeProduct && (
                                        <td className="px-2 py-2 text-left align-top break-words" rowSpan={spanProduct[index]}>
                                            {item.productGroup || item.product?.productType ? (
                                                <span className="font-normal">{item.productGroup || item.product?.productType} - </span>
                                            ) : ''}
                                            <span className="font-bold">{productName}</span>
                                            {specsText && <div className="italic text-slate-700 mt-0.5 font-normal">{specsText.replace(/MM/ig, 'mm')}</div>}
                                            {(item.poReference || item.soReference) && (
                                                <div className="text-[11px] font-semibold text-slate-600 mt-1 flex flex-col gap-0.5">
                                                    {item.poReference && <div>PO: <span className="text-slate-800">{item.poReference}</span></div>}
                                                    {item.soReference && <div>SO: <span className="text-slate-800">{item.soReference}</span></div>}
                                                </div>
                                            )}
                                            {isDiffDeadline && (
                                                <div className="font-bold text-red-600 mt-0.5">
                                                    Hạn giao: {fmtDMY(itemDeadline)}
                                                </div>
                                            )}
                                        </td>
                                    )}
                                    {showMergeMatProc && (
                                        <td className="px-2 py-2 text-left align-top leading-tight break-words" rowSpan={spanMatProc[index]}>
                                            <div className="whitespace-pre-wrap mb-1">{processText !== '—' && processText ? processText : (parseSpecs(item.product?.specs) || parseSpecs(item.specs) || material || '—')}</div>
                                            <div className="text-slate-800 text-[11px]">
                                                {material !== '—' && <div className="mb-0.5"><span className="font-semibold">Chất liệu:</span> {material}</div>}
                                                <div className="mt-0.5 text-[11px]">
                                                    {printSize !== '—' && <span><span className="font-semibold">Khổ in:</span> <span className="text-red-700 font-bold">{printSize}</span></span>}
                                                    {purchaseSize !== '—' && <span className="ml-3"><span className="font-semibold">Khổ mua:</span> {purchaseSize}</span>}
                                                    {outs !== '—' && <span className="ml-3"><span className="font-semibold">Số bát:</span> <span className="font-bold">{outs}</span></span>}
                                                </div>
                                            </div>
                                        </td>
                                    )}
                                    {showImages && showMergeProduct && (
                                        <td className="px-1 py-1 align-top" rowSpan={spanProduct[index]}>
                                            {(item.product?.image || item.image) ? (
                                                <img src={item.product?.image || item.image} alt={productName} className="w-[70px] h-[70px] object-contain mx-auto" />
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                    )}
                                    <td className="px-1 py-2 font-bold text-blue-700 align-top whitespace-nowrap">{qty.toLocaleString('vi-VN')}</td>
                                    <td className="px-1 py-2 text-center align-top border-l border-black font-semibold text-slate-700 whitespace-nowrap">{item.stockQty ? Number(item.stockQty).toLocaleString('vi-VN') : '0'}</td>
                                    <td className="px-1 py-2 text-center align-top border-l border-black font-semibold text-slate-700 whitespace-nowrap">{item.allowancePercent ? `+${item.allowancePercent}%` : ''}</td>
                                    <td className="px-1 py-2 text-center align-top border-l border-black text-blue-800 font-bold whitespace-nowrap">{item.lsxQty ? item.lsxQty.toLocaleString('vi-VN') : ''}</td>
                                    <td className="px-2 py-2 text-left text-red-600 italic leading-tight align-top break-words">{prodNotes}</td>
                                </tr>
                            );
                        });
                    })()}
                </tbody>
            </table>
                );
            })()}

            {/* ========== SIGNATURES ========== */}
            <div className="flex justify-between mt-10 text-[11px] text-center px-10 break-inside-avoid">
                <div>
                    <div className="font-bold mb-16">NGƯỜI LẬP YÊU CẦU</div>
                    <div className="text-slate-500">(Ký & ghi rõ họ tên)</div>
                </div>
                <div>
                    <div className="font-bold mb-16">CHUYÊN VIÊN KẾ HOẠCH</div>
                    <div className="text-slate-500">(Ký & ghi rõ họ tên)</div>
                </div>
                <div>
                    <div className="font-bold mb-16">GIÁM ĐỐC SẢN XUẤT</div>
                    <div className="text-slate-500">(Ký, duyệt)</div>
                </div>
            </div>
        </div>
    );
}
